// Thin typed fetch wrapper. All API calls go through here so the base
// URL, credentials mode, and error handling live in exactly one place.

import type {
  Service,
  ServiceLocation,
  Location,
  Order,
  Invoice,
  FAQ,
  CreateOrderRequest,
  CreateRazorpayOrderResponse,
  AdminDashboardStats,
  PaginatedResult,
  User,
  ConsultationBooking,
  CreateConsultationBookingRequest,
  CreateConsultationRazorpayOrderResponse,
} from '@bharat-consultancy/shared';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const bearerToken = localStorage.getItem('bearer_token');
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    credentials: 'include', // fallback for same-domain cookie scenarios
    headers: {
      'Content-Type': 'application/json',
      ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'unknown', message: res.statusText }));
    throw new ApiError(res.status, body.error ?? 'unknown', body.message ?? res.statusText);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export const api = {
  // --- Public content ---------------------------------------------------
  services: {
    list: () => request<{ services: Service[] }>('/api/services'),
    featured: () => request<{ services: Service[] }>('/api/services/featured'),
    globalFaqs: () => request<{ faqs: FAQ[] }>('/api/services/faqs'),
    bySlug: (slug: string) =>
      request<{ service: Service; availableLocations: (ServiceLocation & { location: Location })[] }>(
        `/api/services/${slug}`
      ),
  },

  locations: {
    list: () => request<{ locations: Location[] }>('/api/locations'),
    byUrlSlug: (urlSlug: string) => request<{ serviceLocation: ServiceLocation }>(`/api/locations/${urlSlug}`),
  },

  orders: {
    create: (body: CreateOrderRequest) =>
      request<{ order: Order }>('/api/orders', { method: 'POST', body: JSON.stringify(body) }),
    list: () => request<{ orders: Order[] }>('/api/orders'),
    get: (id: string) => request<{ order: Order; invoice: Invoice | null }>(`/api/orders/${id}`),
    pay: (id: string) => request<CreateRazorpayOrderResponse>(`/api/orders/${id}/pay`, { method: 'POST' }),
  },

  invoices: {
    downloadUrl: (orderId: string) => `${API_BASE}/api/invoices/${orderId}/download`,
  },

  consultations: {
    create: (body: CreateConsultationBookingRequest) =>
      request<{ booking: ConsultationBooking }>('/api/consultations', { method: 'POST', body: JSON.stringify(body) }),
    get: (id: string) => request<{ booking: ConsultationBooking }>(`/api/consultations/${id}`),
    pay: (id: string) =>
      request<CreateConsultationRazorpayOrderResponse>(`/api/consultations/${id}/pay`, { method: 'POST' }),
  },

  documents: {
    upload: async (orderId: string, docType: string, file: File) => {
      const form = new FormData();
      form.append('orderId', orderId);
      form.append('docType', docType);
      form.append('file', file);
      const bearerToken = localStorage.getItem('bearer_token');
      const res = await fetch(`${API_BASE}/api/documents/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {},
        body: form,
      });
      if (!res.ok) throw new ApiError(res.status, 'upload_failed', 'Document upload failed.');
      return res.json();
    },
    listForOrder: (orderId: string) => request<{ documents: unknown[] }>(`/api/documents/order/${orderId}`),
  },

  leads: {
    create: (body: { name: string; email?: string; phone: string; serviceInterest?: string; message?: string }) =>
      request<{ success: boolean; id: string }>('/api/leads', { method: 'POST', body: JSON.stringify(body) }),
  },

  support: {
    create: (body: { orderId?: string; subject: string; message: string }) =>
      request<{ success: boolean; ticketId: string }>('/api/support', { method: 'POST', body: JSON.stringify(body) }),
    list: () => request<{ tickets: unknown[] }>('/api/support'),
  },

  // --- Admin --------------------------------------------------------------
  admin: {
    dashboard: () => request<{ stats: AdminDashboardStats }>('/api/admin/dashboard'),

    customers: {
      list: (params: { search?: string; page?: number; pageSize?: number } = {}) =>
        request<PaginatedResult<User>>(`/api/admin/customers?${new URLSearchParams(params as Record<string, string>)}`),
      get: (id: string) => request<{ customer: User; orders: Order[] }>(`/api/admin/customers/${id}`),
      update: (id: string, body: { name?: string; phone?: string }) =>
        request<{ customer: User }>(`/api/admin/customers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    },

    orders: {
      list: (params: { status?: string; search?: string; page?: number; pageSize?: number } = {}) =>
        request<PaginatedResult<Order>>(`/api/admin/orders?${new URLSearchParams(params as Record<string, string>)}`),
      get: (id: string) => request<{ order: Order; invoice: Invoice | null; notes: unknown[] }>(`/api/admin/orders/${id}`),
      updateStatus: (id: string, status: string) =>
        request<{ order: Order }>(`/api/admin/orders/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
      allowedTransitions: (id: string) =>
        request<{ allowedTransitions: string[] }>(`/api/admin/orders/${id}/allowed-transitions`),
      assign: (id: string, executiveId: string) =>
        request<{ order: Order }>(`/api/admin/orders/${id}/assign`, { method: 'PATCH', body: JSON.stringify({ executiveId }) }),
      addNote: (id: string, note: string) =>
        request<{ success: boolean; noteId: string }>(`/api/admin/orders/${id}/notes`, { method: 'POST', body: JSON.stringify({ note }) }),
      resendInvoice: (id: string) =>
        request<{ success: boolean; invoice: Invoice }>(`/api/admin/orders/${id}/resend-invoice`, { method: 'POST' }),
    },

    services: {
      list: () => request<{ services: Service[] }>('/api/admin/services'),
      get: (slug: string) => request<{ service: Service }>(`/api/admin/services/${slug}`),
      create: (body: Record<string, unknown>) =>
        request<{ success: boolean; id: string; slug: string }>('/api/admin/services', { method: 'POST', body: JSON.stringify(body) }),
      updateStatus: (id: string, status: string) =>
        request<{ success: boolean }>(`/api/admin/services/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
      remove: (id: string) => request<{ success: boolean }>(`/api/admin/services/${id}`, { method: 'DELETE' }),
    },

    payments: {
      list: (params: { page?: number; pageSize?: number } = {}) =>
        request<PaginatedResult<unknown>>(`/api/admin/payments?${new URLSearchParams(params as Record<string, string>)}`),
      revenueReport: () => request<{ byMonth: unknown[]; byStatus: unknown[] }>('/api/admin/payments/revenue-report'),
    },

    content: {
      faqs: (serviceId?: string) =>
        request<{ faqs: FAQ[] }>(`/api/admin/content/faqs${serviceId ? `?serviceId=${serviceId}` : ''}`),
      createFaq: (body: { serviceId?: string; question: string; answer: string; sortOrder?: number }) =>
        request<{ success: boolean; id: string }>('/api/admin/content/faqs', { method: 'POST', body: JSON.stringify(body) }),
      updateSeo: (serviceId: string, body: { seoTitle?: string; seoDescription?: string }) =>
        request<{ success: boolean }>(`/api/admin/content/services/${serviceId}/seo`, { method: 'PATCH', body: JSON.stringify(body) }),
    },

    leads: {
      list: (params: { status?: string; page?: number; pageSize?: number } = {}) =>
        request<PaginatedResult<unknown>>(`/api/admin/leads?${new URLSearchParams(params as Record<string, string>)}`),
      updateStatus: (id: string, status: string) =>
        request<{ success: boolean }>(`/api/admin/leads/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
    },

    consultations: {
      list: (params: { status?: string; search?: string; page?: number; pageSize?: number } = {}) =>
        request<PaginatedResult<ConsultationBooking>>(
          `/api/admin/consultations?${new URLSearchParams(params as Record<string, string>)}`
        ),
      get: (id: string) => request<{ booking: ConsultationBooking }>(`/api/admin/consultations/${id}`),
      updateStatus: (id: string, status: string) =>
        request<{ booking: ConsultationBooking }>(`/api/admin/consultations/${id}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status }),
        }),
      allowedTransitions: (id: string) =>
        request<{ allowedTransitions: string[] }>(`/api/admin/consultations/${id}/allowed-transitions`),
      setNote: (id: string, note: string) =>
        request<{ booking: ConsultationBooking }>(`/api/admin/consultations/${id}/note`, {
          method: 'PATCH',
          body: JSON.stringify({ note }),
        }),
    },
  },
};

export { ApiError };
